import type { HireFee, JobsSubscriptionStatus, PricingEnum } from "@sidegigllc/api/client";

/** A missing payload or pricing reads as legacy, so those screens stay as they are today. */
export const hireFeePricing = (hireFee: Pick<HireFee, "pricing"> | null | undefined): PricingEnum =>
  hireFee?.pricing ?? "legacy";

export const isOnJobsPlan = (status: JobsSubscriptionStatus | undefined) => status?.plan === "SUBSCRIPTION";

/** For screens without a `hire_fee` payload: the location's plan, mapped onto the same three versions. */
export const pricingForJobsStatus = (status: JobsSubscriptionStatus | undefined): PricingEnum => {
  if (!isOnJobsPlan(status)) return "legacy";
  return status?.access === "FULL" ? "subscribed" : "not_subscribed";
};

/**
 * The hire fee line on Jobs plan screens. Null for legacy, which keeps the per-hire fee widgets.
 * `pricing` doesn't say whether a trial is available, so the trial is offered only when the location's status does.
 */
export const jobsPlanHireFeeMessage = (
  pricing: PricingEnum,
  proFirstName?: string,
  status?: JobsSubscriptionStatus,
): string | null => {
  const name = proFirstName || "this pro";
  switch (pricing) {
    case "subscribed":
      return "Hire fee: $0. Hiring is included in your Jobs plan.";
    case "not_subscribed":
      if (status?.can_start_trial) return `Hire ${name} with a Jobs plan: $250/month, with a 7-day free trial.`;
      if (status?.can_resubscribe) return `Resubscribe to your Jobs plan to hire ${name}.`;
      return `Hire ${name} with a Jobs plan: $250/month.`;
    default:
      return null;
  }
};

/** The Jobs list zero state on the Jobs plan. Undefined for legacy locations, which keep today's intro. */
export const jobsPlanZeroStateMessage = (status: JobsSubscriptionStatus | undefined): string | undefined => {
  if (!isOnJobsPlan(status)) return undefined;
  if (status?.access === "FULL")
    return "Post jobs for free. Seeing applicants and hiring is included in your Jobs plan.";
  if (status?.can_start_trial) {
    return "Post jobs for free. See applicants and hire for $250/month per location. Your first 7 days are free.";
  }
  if (status?.can_resubscribe) return "Post jobs for free. Resubscribe to your Jobs plan to see applicants and hire.";
  return "Post jobs for free. See applicants and hire for $250/month per location.";
};

export const archiveJobMessage = (pendingOffersCount: number, onJobsPlan: boolean): string => {
  const jobsPlanNote = "You'll still see its applicants while you have a Jobs plan.";
  if (pendingOffersCount === 0) {
    return onJobsPlan
      ? `Archiving closes this job to new applicants. ${jobsPlanNote}`
      : "Archiving a job hides it from pros (no new applications) but keeps your existing applicant pipeline fully accessible for viewing profiles, messaging and interviews, and hiring.";
  }
  const cancelled =
    pendingOffersCount === 1
      ? "This job has 1 pending offer. Archiving it cancels it."
      : `This job has ${pendingOffersCount} pending offers. Archiving it cancels them.`;
  return onJobsPlan
    ? `${cancelled} ${jobsPlanNote}`
    : `${cancelled} You will not be charged for any declined or canceled offers, and all credits will remain available.`;
};
